import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from '@tanstack/react-query';
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { queryKeys } from '@/lib/query-keys';

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useContactOptions } from "@/hooks/useContactOptions";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { MultiEntryField } from "@/components/contacts/create/MultiEntryField";
import { AddressAutocomplete } from "@/components/contacts/create/AddressAutocomplete";
import { ExpandableCustomFields } from "@/components/contacts/create/ExpandableCustomFields";
import { ExpandableAddSection } from "@/components/contacts/create/ExpandableAddSection";
import { InlineNoteInput } from "@/components/contacts/create/InlineNoteInput";
import { InlineEventInput } from "@/components/contacts/create/InlineEventInput";
import { InlineDateInput } from "@/components/contacts/create/InlineDateInput";
import { InlineWebsiteInput } from "@/components/contacts/create/InlineWebsiteInput";
import { InlineGroupInput } from "@/components/contacts/create/InlineGroupInput";
import { InlineConnectionInput } from "@/components/contacts/create/InlineConnectionInput";
import { WorkspaceFieldAutocomplete } from "@/components/contacts/create/WorkspaceFieldAutocomplete";
import { captureError } from "@/lib/sentry";
import type { CreateContactFormValues } from "@/types/contact";

const PHONE_LABELS = ['mobile', 'main', 'business', 'home', 'fax', 'assistant', 'additional'] as const;
const EMAIL_LABELS = ['work', 'personal', 'assistant', 'additional'] as const;
const ADDRESS_LABELS = ['home', 'work', 'business', 'main', 'additional'] as const;
const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'Rev.'] as const;

const createContactSchema = z.object({
  title: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  company_name: z.string(),
  job_title: z.string(),
  industry: z.string(),
  is_company: z.boolean(),
  phones: z.array(z.object({ label: z.string(), phone_number: z.string() })),
  emails: z.array(z.object({
    label: z.string(),
    email: z.string().refine(
      (val) => val.trim() === '' || z.string().email().safeParse(val).success,
      { message: "Invalid email format" }
    ),
  })),
  addresses: z.array(z.object({
    label: z.string(),
    formatted: z.string(),
    address_line1: z.string(),
    address_line2: z.string(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    country: z.string(),
    lat: z.string().nullable(),
    long: z.string().nullable(),
  })),
  custom_fields: z.record(z.string()),
  type: z.string(),
  rank: z.string(),
  relationship: z.string(),
  status: z.string(),
  note: z.string(),
  event_title: z.string(),
  event_date: z.string(),
  important_date: z.string(),
  important_date_label: z.string(),
  websites: z.array(z.object({ url: z.string(), label: z.string() })),
  group_name: z.string(),
  connection_name: z.string(),
  connection_contact_id: z.string(),
  connection_relationship: z.string(),
}).refine(
  (data) => data.first_name.trim() || data.last_name.trim() || data.company_name.trim(),
  { message: "At least one name field is required", path: ["first_name"] }
);

interface CreateContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId?: string;
  onSuccess?: () => void;
}

const DEFAULT_VALUES: CreateContactFormValues = {
  title: "",
  first_name: "",
  last_name: "",
  company_name: "",
  job_title: "",
  industry: "",
  is_company: false,
  phones: [{ label: "mobile", phone_number: "" }],
  emails: [{ label: "work", email: "" }],
  addresses: [],
  custom_fields: {},
  type: "",
  rank: "",
  relationship: "",
  status: "Not Yet Contacted",
  note: "",
  event_title: "",
  event_date: "",
  important_date: "",
  important_date_label: "",
  websites: [],
  group_name: "",
  connection_name: "",
  connection_contact_id: "",
  connection_relationship: "",
};

export function CreateContactDialog({
  open,
  onOpenChange,
  workspaceId,
  onSuccess,
}: CreateContactDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const { options, loading: optionsLoading } = useContactOptions();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const toggleSection = (section: string) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  const form = useForm<CreateContactFormValues>({
    resolver: zodResolver(createContactSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({ control: form.control, name: "phones" });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({ control: form.control, name: "emails" });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control: form.control, name: "addresses" });

  const {
    fields: websiteFields,
    append: appendWebsite,
    remove: removeWebsite,
  } = useFieldArray({ control: form.control, name: "websites" });

  const handleEnterSubmit = (e: React.KeyboardEvent<HTMLFormElement>) => {
    const target = e.target as HTMLElement;
    if (
      e.key === 'Enter' &&
      !e.ctrlKey &&
      !e.metaKey &&
      target.tagName === 'INPUT' &&
      target.getAttribute('role') !== 'combobox' &&
      target.getAttribute('type') !== 'button'
    ) {
      e.preventDefault();
      // Submit the form (same as clicking "Create contact")
      form.handleSubmit((data) => handleSubmit(data, true))();
    }
  };

  const handleSubmit = async (data: CreateContactFormValues, shouldClose: boolean) => {
    setIsLoading(true);

    try {
      if (!workspaceId) {
        throw new Error("No workspace selected");
      }

      // 1. Insert contact row
      const { data: newContact, error } = await supabase
        .from("contacts")
        .insert([{
          title: data.title || null,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
          job_title: data.job_title || null,
          industry: data.industry || null,
          is_company: !!(data.company_name.trim() && !data.first_name.trim() && !data.last_name.trim()),
          type: data.type || null,
          rank: data.rank || null,
          relationship: data.relationship || null,
          status: data.status || 'Not Yet Contacted',
          workspace_id: workspaceId,
          // Canonical 'NO' (text). Previously wrote boolean `false` which
          // PostgREST coerces to the text 'false' — a non-canonical drift
          // pattern. 'NO' matches BulkEditDialog + email-unsubscribe writes.
          remarket: 'NO',
          created_by: user?.id || null,
        }])
        .select()
        .single();

      if (error) throw error;

      // 2. Filter out empty entries
      const validPhones = data.phones.filter(p => p.phone_number.trim());
      const validEmails = data.emails.filter(e => e.email.trim());
      const validAddresses = data.addresses.filter(a => a.address_line1.trim() || a.city.trim() || a.country.trim() || a.formatted.trim());
      const validCustomFields = Object.entries(data.custom_fields).filter(([, v]) => v.trim());

      // 3. Insert child records in parallel
      const childInserts: PromiseLike<{ error: unknown }>[] = [];

      if (validPhones.length > 0) {
        childInserts.push(
          supabase.from('phone_numbers').insert(
            validPhones.map((p, i) => ({
              contact_id: newContact.id,
              phone_number: p.phone_number,
              isPrimary: i === 0,
              label: p.label,
            }))
          )
        );
      }

      if (validEmails.length > 0) {
        childInserts.push(
          supabase.from('email_address').insert(
            validEmails.map((e, i) => ({
              contact_id: newContact.id,
              email: e.email,
              is_primary: i === 0,
              label: e.label,
            }))
          )
        );
      }

      if (validAddresses.length > 0) {
        childInserts.push(
          supabase.from('physical_addresses').insert(
            validAddresses.map((a, i) => ({
              contact_id: newContact.id,
              address_line1: a.address_line1,
              address_line2: a.address_line2,
              city: a.city,
              state: a.state,
              postal_code: a.postal_code,
              country: a.country,
              lat: a.lat,
              long: a.long,
              is_primary: i === 0,
              label: a.label,
            }))
          )
        );
      }

      if (validCustomFields.length > 0) {
        childInserts.push(
          supabase.from('custom_field_values').insert(
            validCustomFields.map(([fieldId, value]) => ({
              contact_id: newContact.id,
              custom_field_id: fieldId,
              value,
            }))
          )
        );
      }

      // Note
      if (data.note.trim()) {
        childInserts.push(
          supabase.from('notes').insert({
            contact_id: newContact.id,
            content: data.note,
            user_id: user?.id,
          })
        );
      }

      // Event/Activity
      if (data.event_title.trim()) {
        childInserts.push(
          supabase.from('activities').insert({
            contact_id: newContact.id,
            title: data.event_title,
            start_date: data.event_date || new Date().toISOString(),
            type: 'in_person',
            workspace_id: workspaceId,
            user_id: user?.id,
            scheduled: false,
            completed: true,
          })
        );
      }

      // Important Date
      if (data.important_date.trim()) {
        childInserts.push(
          supabase.from('important_dates').insert({
            contact_id: newContact.id,
            workspace_id: workspaceId,
            created_by: user?.id,
            date: data.important_date,
            label: data.important_date_label?.trim() || null,
          })
        );
      }

      // Websites
      const validWebsites = data.websites.filter(w => w.url.trim());
      if (validWebsites.length > 0) {
        childInserts.push(
          supabase.from('website_domains').insert(
            validWebsites.map((w, i) => ({
              contact_id: newContact.id,
              website_domain: w.url,
              is_primary: i === 0,
              label: w.label || null,
            }))
          )
        );
      }

      // Run all child inserts in parallel
      const results = await Promise.allSettled(childInserts);
      const failures = results.filter(r =>
        r.status === 'rejected' ||
        (r.status === 'fulfilled' && r.value?.error)
      );

      // Group (sequential — need to find/create group first)
      if (data.group_name.trim()) {
        try {
          const trimmedGroup = data.group_name.trim().toLowerCase();
          const { data: existingGroup } = await supabase
            .from('groups')
            .select('id')
            .eq('workspace_id', workspaceId)
            .eq('group_name', trimmedGroup)
            .single();

          let groupId = existingGroup?.id;
          if (!groupId) {
            const { data: newGroup } = await supabase
              .from('groups')
              .insert({ workspace_id: workspaceId, group_name: trimmedGroup, created_by: user?.id })
              .select()
              .single();
            groupId = newGroup?.id;
          }
          if (groupId) {
            await supabase.from('contact_group_members').insert({
              group_id: groupId,
              contact_id: newContact.id,
            });
          }
        } catch {
          /* ignore group errors */
        }
      }

      if (failures.length > 0) {
        toast({ description: "Contact created, but some details failed to save", variant: "destructive" });
      } else {
        toast({ description: "Contact created" });
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });

      if (shouldClose) {
        form.reset(DEFAULT_VALUES);
        setExpandedSections({});
        onOpenChange(false);
        onSuccess?.();
      } else {
        // "Create & add another" — preserve smart fields
        const preservedType = data.type;
        const preservedRank = data.rank;
        const preservedRelationship = data.relationship;
        const preservedStatus = data.status;
        form.reset(DEFAULT_VALUES);
        setExpandedSections({});
        // Restore smart fields after reset
        form.setValue('type', preservedType);
        form.setValue('rank', preservedRank);
        form.setValue('relationship', preservedRelationship);
        form.setValue('status', preservedStatus);
        // Reset phones/emails to default empty entries
        form.setValue('phones', [{ label: 'mobile', phone_number: '' }]);
        form.setValue('emails', [{ label: 'work', email: '' }]);
        // Focus first name field
        setTimeout(() => {
          const firstInput = document.querySelector<HTMLInputElement>('[name="first_name"]');
          firstInput?.focus();
        }, 100);
        // Still call onSuccess to refresh the contacts list
        onSuccess?.();
      }
    } catch (err) {
      console.error('Error creating contact:', err);
      captureError(err as Error, { feature: 'create-contact' });
      toast({
        title: "Error",
        description: "Failed to create contact. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] flex flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Contact</DialogTitle>
          <DialogDescription>
            Add a new contact to your workspace
          </DialogDescription>
        </DialogHeader>

        {optionsLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Form {...form}>
            <form onKeyDown={handleEnterSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-4 px-1 pt-2">
                {/* Row 1: Title | First Name | Last Name | Job Title */}
                <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Title" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TITLE_OPTIONS.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={() => (
                      <FormItem>
                        <FormControl>
                          <Input {...form.register('first_name')} placeholder="First Name" className="h-8 text-sm" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={() => (
                      <FormItem>
                        <FormControl>
                          <Input {...form.register('last_name')} placeholder="Last Name" className="h-8 text-sm" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="job_title"
                    render={() => (
                      <FormItem>
                        <FormControl>
                          <Input {...form.register('job_title')} placeholder="Job Title" className="h-8 text-sm" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Row 2: Company Name | Industry */}
                <div className="grid grid-cols-2 gap-2">
                  <WorkspaceFieldAutocomplete
                    value={form.watch('company_name')}
                    onChange={(value) => form.setValue('company_name', value)}
                    workspaceId={workspaceId || ''}
                    field="company_name"
                    placeholder="Company Name"
                    className="h-8 text-sm"
                  />
                  <WorkspaceFieldAutocomplete
                    value={form.watch('industry')}
                    onChange={(value) => form.setValue('industry', value)}
                    workspaceId={workspaceId || ''}
                    field="industry"
                    placeholder="Industry"
                    className="h-8 text-sm"
                  />
                </div>

                {/* Phone Numbers */}
                <MultiEntryField
                  entries={form.watch('phones')}
                  onAdd={() => appendPhone({ label: 'mobile', phone_number: '' })}
                  onRemove={(i) => phoneFields.length > 1 ? removePhone(i) : form.setValue(`phones.${i}`, { label: 'mobile', phone_number: '' })}
                  onChange={(i, field, value) => form.setValue(`phones.${i}.${field}` as `phones.${number}.label` | `phones.${number}.phone_number`, value)}
                  labelOptions={PHONE_LABELS}
                  labelField="label"
                  renderInput={(_entry, i) => (
                    <Input
                      {...form.register(`phones.${i}.phone_number`)}
                      placeholder="Phone number"
                      className="h-8 text-sm flex-1"
                    />
                  )}
                  addLabel="Add phone"
                  sectionLabel="Phone Numbers"
                />

                {/* Email Addresses */}
                <MultiEntryField
                  entries={form.watch('emails')}
                  onAdd={() => appendEmail({ label: 'work', email: '' })}
                  onRemove={(i) => emailFields.length > 1 ? removeEmail(i) : form.setValue(`emails.${i}`, { label: 'work', email: '' })}
                  onChange={(i, field, value) => form.setValue(`emails.${i}.${field}` as `emails.${number}.label` | `emails.${number}.email`, value)}
                  labelOptions={EMAIL_LABELS}
                  labelField="label"
                  renderInput={(_entry, i) => (
                    <Input
                      {...form.register(`emails.${i}.email`)}
                      placeholder="Email address"
                      type="email"
                      className="h-8 text-sm flex-1"
                    />
                  )}
                  addLabel="Add email"
                  sectionLabel="Email Addresses"
                />

                {/* Smart Fields — grey card with labels */}
                <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
                  <span className="text-xs font-medium text-muted-foreground">Smart Fields</span>
                  <div className="grid grid-cols-4 gap-2">
                    <FormField
                      control={form.control}
                      name="relationship"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel className="text-[10px] text-muted-foreground">Relationship</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-8 text-sm bg-background">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {options.relationship.map(value => (
                                <SelectItem key={value} value={value}>{value}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel className="text-[10px] text-muted-foreground">Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-8 text-sm bg-background">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {options.type.map(value => (
                                <SelectItem key={value} value={value}>{value}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="rank"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <FormLabel className="text-[10px] text-muted-foreground">Rank</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-8 text-sm bg-background">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {options.rank.map(value => (
                                <SelectItem key={value} value={value}>{value}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="space-y-1" data-tour-id="create-contact-modal.status-field">
                          <FormLabel className="text-[10px] text-muted-foreground">Status</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-8 text-sm bg-background">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {options.status.map(value => (
                                <SelectItem key={value} value={value}>{value}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Expandable Sections */}
                <div className="space-y-1">
                  {/* + Add Physical Address */}
                  <ExpandableAddSection
                    label="Add Physical Address"
                    expanded={!!expandedSections.address}
                    onToggle={() => {
                      toggleSection('address');
                      if (!expandedSections.address && addressFields.length === 0) {
                        appendAddress({
                          label: 'work', formatted: '', address_line1: '',
                          address_line2: '', city: '', state: '',
                          postal_code: '', country: '', lat: null, long: null,
                        });
                      }
                    }}
                  >
                    <MultiEntryField
                      entries={form.watch('addresses')}
                      onAdd={() => appendAddress({
                        label: 'work',
                        formatted: '',
                        address_line1: '',
                        address_line2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: '',
                        lat: null,
                        long: null,
                      })}
                      onRemove={(i) => removeAddress(i)}
                      onChange={(i, field, value) => form.setValue(`addresses.${i}.${field}` as any, value)}
                      labelOptions={ADDRESS_LABELS}
                      labelField="label"
                      renderInput={(_entry, i) => (
                        <AddressAutocomplete
                          value={form.watch(`addresses.${i}.formatted`)}
                          address_line1={form.watch(`addresses.${i}.address_line1`)}
                          address_line2={form.watch(`addresses.${i}.address_line2`)}
                          city={form.watch(`addresses.${i}.city`)}
                          state={form.watch(`addresses.${i}.state`)}
                          postal_code={form.watch(`addresses.${i}.postal_code`)}
                          country={form.watch(`addresses.${i}.country`)}
                          lat={form.watch(`addresses.${i}.lat`)}
                          long={form.watch(`addresses.${i}.long`)}
                          onChange={(address) => {
                            form.setValue(`addresses.${i}.formatted`, address.formatted);
                            form.setValue(`addresses.${i}.address_line1`, address.address_line1);
                            form.setValue(`addresses.${i}.address_line2`, address.address_line2);
                            form.setValue(`addresses.${i}.city`, address.city);
                            form.setValue(`addresses.${i}.state`, address.state);
                            form.setValue(`addresses.${i}.postal_code`, address.postal_code);
                            form.setValue(`addresses.${i}.country`, address.country);
                            form.setValue(`addresses.${i}.lat`, address.lat);
                            form.setValue(`addresses.${i}.long`, address.long);
                          }}
                          onFieldChange={(field, value) => form.setValue(`addresses.${i}.${field}` as any, value)}
                        />
                      )}
                      addLabel="Add another address"
                      sectionLabel=""
                    />
                  </ExpandableAddSection>

                  {/* + Add Note */}
                  <ExpandableAddSection
                    label="Add Note"
                    expanded={!!expandedSections.note}
                    onToggle={() => toggleSection('note')}
                  >
                    <InlineNoteInput
                      value={form.watch('note')}
                      onChange={(value) => form.setValue('note', value)}
                    />
                  </ExpandableAddSection>

                  {/* + Add Event */}
                  <ExpandableAddSection
                    label="Add Event"
                    expanded={!!expandedSections.event}
                    onToggle={() => toggleSection('event')}
                  >
                    <InlineEventInput
                      title={form.watch('event_title')}
                      date={form.watch('event_date')}
                      onTitleChange={(value) => form.setValue('event_title', value)}
                      onDateChange={(value) => form.setValue('event_date', value)}
                    />
                  </ExpandableAddSection>

                  {/* + Add Important Date */}
                  <ExpandableAddSection
                    label="Add Important Date"
                    expanded={!!expandedSections.importantDate}
                    onToggle={() => toggleSection('importantDate')}
                  >
                    <InlineDateInput
                      date={form.watch('important_date')}
                      label={form.watch('important_date_label')}
                      onDateChange={(value) => form.setValue('important_date', value)}
                      onLabelChange={(value) => form.setValue('important_date_label', value)}
                    />
                  </ExpandableAddSection>

                  {/* + Add Website/URL */}
                  <ExpandableAddSection
                    label="Add Website/URL"
                    expanded={!!expandedSections.website}
                    onToggle={() => {
                      toggleSection('website');
                      // Add initial entry when expanding if none exist
                      if (!expandedSections.website && websiteFields.length === 0) {
                        appendWebsite({ url: '', label: 'website' });
                      }
                    }}
                  >
                    <InlineWebsiteInput
                      entries={websiteFields.map((f) => ({ url: form.watch(`websites.${websiteFields.indexOf(f)}.url`) ?? '', label: form.watch(`websites.${websiteFields.indexOf(f)}.label`) ?? 'website' }))}
                      onAdd={() => appendWebsite({ url: '', label: 'website' })}
                      onRemove={(index) => removeWebsite(index)}
                      onChange={(index, field, value) => form.setValue(`websites.${index}.${field}` as `websites.${number}.url` | `websites.${number}.label`, value)}
                    />
                  </ExpandableAddSection>

                  {/* + Add to Group */}
                  <ExpandableAddSection
                    label="Add to Group"
                    expanded={!!expandedSections.group}
                    onToggle={() => toggleSection('group')}
                  >
                    <InlineGroupInput
                      value={form.watch('group_name')}
                      onChange={(value) => form.setValue('group_name', value)}
                      workspaceId={workspaceId || ''}
                    />
                  </ExpandableAddSection>

                  {/* + Add Connection */}
                  <ExpandableAddSection
                    label="Add Connection"
                    expanded={!!expandedSections.connection}
                    onToggle={() => toggleSection('connection')}
                  >
                    <InlineConnectionInput
                      contactName={form.watch('connection_name')}
                      relationshipType={form.watch('connection_relationship')}
                      onContactNameChange={(value) => form.setValue('connection_name', value)}
                      onContactIdChange={(id) => form.setValue('connection_contact_id', id)}
                      onRelationshipTypeChange={(value) => form.setValue('connection_relationship', value)}
                      workspaceId={workspaceId || ''}
                    />
                  </ExpandableAddSection>

                  {/* + Add Custom Field */}
                  {workspaceId && (
                    <ExpandableCustomFields
                      workspaceId={workspaceId}
                      values={form.watch('custom_fields')}
                      onChange={(fieldId, value) => form.setValue(`custom_fields.${fieldId}`, value)}
                    />
                  )}
                </div>
              </div>

              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => form.handleSubmit((data) => handleSubmit(data, false))()}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create & add another"
                  )}
                </Button>
                <Button
                  type="button"
                  disabled={isLoading}
                  onClick={() => form.handleSubmit((data) => handleSubmit(data, true))()}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create contact"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
